import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import './JobsPage.css';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Footer from './Footer';
import Header from './Header';
import JobMap from "./JobMap";
import { Helmet } from "react-helmet-async";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [animateKey, setAnimateKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Search inputs (match HomePage approach – no "all" or "sort")
  const [jobTypeSearch, setJobTypeSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [positionSearch, setPositionSearch] = useState('');

  // Suggestions
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [positionSuggestions, setPositionSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [showPositionSuggestions, setShowPositionSuggestions] = useState(false);
  const [showJobTypeSuggestions, setShowJobTypeSuggestions] = useState(false);

  // Refs for outside-click
  const locationDropdownRef = useRef(null);
  const positionDropdownRef = useRef(null);
  const jobTypeDropdownRef = useRef(null);

  const jobTypeOptions = ['Stalni', 'Sezonski'];

  // === Allowed-value helpers (accept only items from dropdowns) ===
const norm = (s) => s.trim().toLowerCase();

// Build canonical maps from fetched suggestions (case-insensitive lookups)
const locationMap = useMemo(() => {
  const m = new Map();
  ['CRO', 'MNE', ...locationSuggestions.map(i => i.job_location)]
    .forEach(v => m.set(norm(v), v));
  return m;
}, [locationSuggestions]);

const positionMap = useMemo(() => {
  const m = new Map();
  positionSuggestions.map(i => i.job_name)
    .forEach(v => m.set(norm(v), v));
  return m;
}, [positionSuggestions]);

// Token utilities
const tokensFrom = (input) =>
  input.split(',').map(t => t.trim()).filter(Boolean);

const filterToAllowed = (tokens, canonMap) => {
  const valid = [];
  const invalid = [];
  tokens.forEach(t => {
    const hit = canonMap.get(norm(t));
    if (hit) {
      if (!valid.includes(hit)) valid.push(hit); // de-dup
    } else {
      invalid.push(t);
    }
  });
  return { valid, invalid };
};

// Commiters for onBlur (clean free text to only allowed values)
const commitLocations = () => {
  const { valid } = filterToAllowed(tokensFrom(locationSearch), locationMap);
  setLocationSearch(valid.join(', ')); // no trailing ", "
};

const commitPositions = () => {
  const { valid } = filterToAllowed(tokensFrom(positionSearch), positionMap);
  setPositionSearch(valid.join(', ')); // no trailing ", "
};


// Block Enter if last token isn't valid yet (optional but nice)
const guardEnter = (e, currentInput, canonMap) => {
  if (e.key === 'Enter') {
    const toks = tokensFrom(currentInput);
    const last = toks[toks.length - 1] || '';
    if (last && !canonMap.get(norm(last))) {
      e.preventDefault(); // don't submit until a valid suggestion is chosen
    }
  }
};


  const getLastToken = (input) => {
    const tokens = input.split(',');
    return tokens[tokens.length - 1].trim();
  };

  // const handleLocationInputChange = (e) => {
  //   setLocationSearch(e.target.value);
  // };

  const handleLocationInputChange = (e) => {
  const validated = validateAlphaInput(e.target.value);
  setLocationSearch(validated);
};

const handlePositionInputChange = (e) => {
  const validated = validateAlphaInput(e.target.value);
  setPositionSearch(validated);
};

const handleJobTypeInputChange = (e) => {
  const validated = validateAlphaInput(e.target.value);
  setJobTypeSearch(validated);
};

const locationInputRef = useRef(null);
const positionInputRef = useRef(null);

const addTrailingWhileEditing = (val) =>
  val && !/,\s*$/.test(val) ? val + ', ' : val;


const validateAlphaInput = (value) => {
  return value.replace(/[^a-zA-Z0-9ćčžšđĆČŽŠĐ,\s\-\/']/g, '');
};



const handleLocationSuggestionClick = (suggestion) => {
  const prev = locationSearch;
  const endsWithComma = prev.trimEnd().endsWith(',');
  let tokens = prev.split(',').map(t => t.trim()).filter(Boolean);

  if (tokens.length && !endsWithComma) {
    tokens[tokens.length - 1] = suggestion;
  } else if (tokens.includes(suggestion)) {
    tokens = tokens.filter(t => t !== suggestion);
  } else {
    tokens.push(suggestion);
  }

  setLocationSearch(tokens.join(', '));
  setShowLocationSuggestions(false);
  requestAnimationFrame(() => locationInputRef.current?.blur());
};


const handlePositionSuggestionClick = (suggestion) => {
  const prev = positionSearch;
  const endsWithComma = prev.trimEnd().endsWith(',');
  let tokens = prev.split(',').map(t => t.trim()).filter(Boolean);

  if (tokens.length && !endsWithComma) {
    tokens[tokens.length - 1] = suggestion;
  } else if (tokens.includes(suggestion)) {
    tokens = tokens.filter(t => t !== suggestion);
  } else {
    tokens.push(suggestion);
  }

  setPositionSearch(tokens.join(', '));
  setShowPositionSuggestions(false);
  requestAnimationFrame(() => positionInputRef.current?.blur());
};

  const handleJobTypeSuggestionClick = (suggestion) => {
    const trimmedSearch = jobTypeSearch.trimEnd();
    const endsWithComma = trimmedSearch.endsWith(',');
    let tokens = jobTypeSearch.split(',').map(t => t.trim()).filter(Boolean);

    if (tokens.length > 0 && tokens[tokens.length - 1] === suggestion && !endsWithComma) {
      setJobTypeSearch(tokens.join(', ') + ', ');
      return;
    }

    if (tokens.includes(suggestion)) {
      tokens = tokens.filter(t => t !== suggestion);
    } else {
      if (!endsWithComma && tokens.length > 0) {
        tokens[tokens.length - 1] = suggestion;
      } else {
        tokens.push(suggestion);
      }
    }
    setJobTypeSearch(tokens.length ? tokens.join(', ') + ', ' : '');
  };

  // Close dropdowns on outside click (deferred so clicks can submit)
  useEffect(() => {
    const inside = (ref, target) => ref?.current && ref.current.contains(target);
    const onDocClick = (e) => {
      if (
        inside(locationDropdownRef, e.target) ||
        inside(positionDropdownRef, e.target) ||
        inside(jobTypeDropdownRef, e.target)
      ) return;

      requestAnimationFrame(() => {
        setShowLocationSuggestions(false);
        setShowPositionSuggestions(false);
        setShowJobTypeSuggestions(false);
      });
    };

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

const handleNewSearch = (e) => {
  if (e) e.preventDefault();

    // 🛡️ Wait until suggestions are in (so filtering works)
  if (locationMap.size === 0 && positionMap.size === 0) {
    // optionally show a toast here
    return;
  }

  // Keep only allowed tokens (case-insensitive)
  const { valid: locValid } = filterToAllowed(tokensFrom(locationSearch), locationMap);
  const { valid: posValid } = filterToAllowed(tokensFrom(positionSearch), positionMap);

  // Reflect cleaned values in the inputs
setLocationSearch(locValid.join(', '));
setPositionSearch(posValid.join(', '));


  // Build URL params using only valid values
  const params = {};
  const jt = jobTypeSearch.trim();
  if (jt === 'Stalni' || jt === 'Sezonski') params.jobType = jt;
  if (locValid.length) params.location = locValid.join(', ');
  if (posValid.length) params.position = posValid.join(', ');

  const queryString = new URLSearchParams(params).toString();
  navigate(`/jobs-page?${queryString}`);
};


  // Suggestions fetch
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/job-locations`);
        setLocationSuggestions(data);
      } catch (err) {
        console.error('Error fetching locations:', err.message);
      }
    };

    const fetchPositions = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/job-names`);
        setPositionSuggestions(data);
      } catch (err) {
        console.error('Error fetching positions:', err.message);
      }
    };

    fetchLocations();
    fetchPositions();
  }, []);

  const hasFilters = useMemo(
  () => Boolean(jobTypeSearch.trim() || locationSearch.trim() || positionSearch.trim()),
  [jobTypeSearch, locationSearch, positionSearch]
);


  // React to URL query (search jobs)
useEffect(() => {
  const queryParams = new URLSearchParams(location.search);

  // Raw params from URL
  const rawJT  = (queryParams.get('jobType') || '').trim();
  const rawLoc = queryParams.get('location') || '';
  const rawPos = queryParams.get('position') || '';

  // Wait until the *relevant* maps are ready
  const locTokens = tokensFrom(rawLoc);
  const posTokens = tokensFrom(rawPos);

  const needLocMap = locTokens.length > 0;
  const needPosMap = posTokens.length > 0;

  if ((needLocMap && locationMap.size === 0) || (needPosMap && positionMap.size === 0)) {
    // Suggestions not ready yet → don't fetch
    return;
  }

  // Validate against allowed values
  const jtValid = rawJT === 'Stalni' || rawJT === 'Sezonski' ? rawJT : '';
  const { valid: locValid } = filterToAllowed(locTokens, locationMap);
  const { valid: posValid } = filterToAllowed(posTokens, positionMap);

  // Reflect sanitized params into inputs (no trailing comma)
  setJobTypeSearch(jtValid);
  setLocationSearch(locValid.join(', '));
  setPositionSearch(posValid.join(', '));

  // Build final params for API using only valid values
  const params = {};
  if (jtValid) params.jobType = jtValid;
  if (locValid.length) params.location = locValid.join(', ');
  if (posValid.length) params.position = posValid.join(', ');

  // ---- prevent stale overwrites
  let alive = true;

  const fetcher = Object.keys(params).length
    ? axios.get(`${API_BASE_URL}/search-jobs`, { params })
    : axios.get(`${API_BASE_URL}/available-jobs`);

    fetcher
      .then(({ data }) => {
        if (!alive) return;

        // Sort jobs by postedAt date descending (newest first)
        const sortedJobs = data.slice().sort((a, b) => {
          const dateA = new Date(a.postedAt);
          const dateB = new Date(b.postedAt);
          return dateB - dateA; // descending
        });

        setJobs(sortedJobs);
        setAnimateKey(prev => prev + 1);
      })

    .catch(err => {
      if (!alive) return;
      console.error('Error fetching jobs:', err.message);
    });

  return () => { alive = false; };
}, [location.search, locationMap, positionMap]);



  // Same clamp as HomePage
  const jobDescriptionStyle = useMemo(
    () => ({
      display: '-webkit-box',
      WebkitLineClamp: 5,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      height: '110px',
    }),
    []
  );


  return (
    <>
      <Helmet>
      <title>Ponude Poslova | StarRecruitment</title>
      <meta
        name="description"
        content="Pregledajte najnovije ponude poslova i pronađite savršeno radno mjesto uz Sezonski Poslovi Star."
      />
      <meta
        name="keywords"
        content="posao, zapošljavanje, karijera, oglasi za posao"
      />
      <meta property="og:title" content="Ponude Poslova | Sezonski Poslovi Star" />
      <meta
        property="og:description"
        content="Pregledajte najnovije poslove, filtrirajte po lokaciji, poziciji i tipu posla."
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.starrecruitment.com/jobs" />
      <meta property="og:image" content="/images/seo-banner.jpg" />
    </Helmet>

      <Header />
      <JobMap />
      <div className="jobs-page-bg-wrapper">

        {/* --- SEARCH (exactly like HomePage) --- */}
        <form className="home-search" onSubmit={handleNewSearch}>
          <div className="search-grid">
          <div className="sf">
            <select
              value={jobTypeSearch}
              onChange={(e) => setJobTypeSearch(e.target.value)}
              className="job-type-select"
              aria-label="Pretražite po tipu posla"
            >
              <option value="">Odaberite tip posla...</option>
              <option value="Stalni">Stalni</option>
              <option value="Sezonski">Sezonski</option>
            </select>
          </div>

            <div className="sf" ref={locationDropdownRef}>
              <input
                type="text"
                placeholder="Pretražite na osnovu lokacije..."
                ref={locationInputRef}
                value={locationSearch}
                onChange={handleLocationInputChange}
                //onFocus={() => setShowLocationSuggestions(true)}
                onFocus={() => {
                setShowLocationSuggestions(true);
                setShowJobTypeSuggestions(false);
                setShowPositionSuggestions(false);
                setLocationSearch(prev => addTrailingWhileEditing(prev)); // <-- add this
                }}
                  onBlur={commitLocations}                                  // ← ADD
                 onKeyDown={(e) => guardEnter(e, locationSearch, locationMap)} // ← ADD
                aria-label="Pretražite na osnovu lokacije"
              />
              {showLocationSuggestions && (
                <ul className="sugs">
                  {(() => {
                    const token = getLastToken(locationSearch);
                    const catalog = ['CRO', 'MNE', ...locationSuggestions.map(i => i.job_location)];
                    const filtered = catalog.filter(label =>
                      !token || label.toLowerCase().includes(token.toLowerCase())
                    );
                    const selectedTokens = locationSearch.split(',').map(t => t.trim()).filter(Boolean);
                    return filtered.map((label) => {
                      const isSelected = selectedTokens.includes(label);
                      const isCode = label === 'CRO' || label === 'MNE';
                      return (
                        <li key={label} onMouseDown={() => handleLocationSuggestionClick(label)}>
                          {isCode ? <strong>{label}</strong> : label}
                          {isSelected && <span className="tick">✓</span>}
                        </li>
                      );
                    });
                  })()}
                </ul>
              )}
            </div>

            <div className="sf" ref={positionDropdownRef}>
              <input
                type="text"
                placeholder="Pretražite po željenoj poziciji..."
                value={positionSearch}
                ref={positionInputRef}
                // onChange={(e) => setPositionSearch(e.target.value)}
                 onChange={handlePositionInputChange}
                //onFocus={() => setShowPositionSuggestions(true)}
                onFocus={() => {
                setShowPositionSuggestions(true);
                setShowLocationSuggestions(false);
                setShowJobTypeSuggestions(false);
                setPositionSearch(prev => addTrailingWhileEditing(prev)); // <-- add this
              }}
                  onBlur={commitPositions}                                   // ← ADD
                onKeyDown={(e) => guardEnter(e, positionSearch, positionMap)} // ← ADD
                aria-label="Pretražite po željenoj poziciji"
              />
              {showPositionSuggestions && (
                <ul className="sugs">
                  {(() => {
                    const token = getLastToken(positionSearch);
                    const names = positionSuggestions.map(i => i.job_name);
                    const filtered = names.filter(name =>
                      !token || name.toLowerCase().includes(token.toLowerCase())
                    );
                    const selectedTokens = positionSearch.split(',').map(t => t.trim()).filter(Boolean);
                    return filtered.map((name) => {
                      const isSelected = selectedTokens.includes(name);
                      return (
                        <li key={name} onMouseDown={() => handlePositionSuggestionClick(name)}>
                          {name} {isSelected && <span className="tick">✓</span>}
                        </li>
                      );
                    });
                  })()}
                </ul>
              )}
            </div>

            <button className="search-btn" type="submit">PRETRAŽI POSLOVE</button>
          </div>
        </form>

        {/* --- JOBS grid (same card pattern as HomePage) --- */}
        <div className="jobs-page-container">
        <div className="search-section">
          {jobs.length === 0 && hasFilters ? (
            <p className="no-jobs-msg">
              Žao nam je, trenutno nema dostupnih poslova sa zadatim kriterijumima.
            </p>
          ) : (
            <ul key={animateKey} className="fade-in">
              {jobs.map((job) => (
                <li key={job.id}>
                  <Link
                    to={`/jobs/${job.id}`}
                    className="job-card-link"
                    style={{ textDecoration: 'none', color: 'inherit', height: '100%' }}
                  >
                    {job.photo && (
                      <img
                        className="job-post-photo-homePage"
                        src={job.photo}
                        alt={job.job_name || "Slika posla"}
                        onError={(e) => { e.target.src = "/images/placeholder.jpg"; }}
                      />
                    )}

                    <div className="job-card-location">
                      <span className="job-card-location-icon">📍</span>
                      <span>{job.job_location}</span>
                      <span
                        className={`job-card-type-pill ${
                          job.job_type === 'Stalni'
                            ? 'job-card-type-stalni'
                            : 'job-card-type-sezonski'
                        }`}
                      >
                        {job.job_type}
                      </span>
                    </div>

                    <h3 className="job-post-title-homePage">{job.job_name}</h3>

                    <div style={jobDescriptionStyle} className="job-post-description-homePage">
                      {job.shortDescription}
                    </div>

                    <div className="homePage-job-card-details">
                      <div className="homePage-detail-item">
                        <div className="homePage-detail-label"><b>Objavljen</b></div>
                        <div className="homePage-detail-value">
                          <span className="icon">📅</span>
                          {job.postedAt ? new Date(job.postedAt).toLocaleDateString() : '-'}
                        </div>
                      </div>
                      <div className="homePage-detail-item">
                        <div className="homePage-detail-label"><b>Početak rada</b></div>
                        <div className="homePage-detail-value">
                          <span className="icon">🗓</span>
                          {job.startDate ? new Date(job.startDate).toLocaleDateString() : '-'}
                        </div>
                      </div>
                      <div className="homePage-detail-item">
                        <div className="homePage-detail-label"><b>Plata</b></div>
                        <div className="homePage-detail-value">
                          <span className="icon">💶</span>€{job.salary}/mj
                        </div>
                      </div>
                      <div className="homePage-detail-item">
                        <div className="homePage-detail-label"><b>Slobodan dan</b></div>
                        <div className="homePage-detail-value">
                          <span className="icon">🆓</span>
                          {job.freeDay ? 'Da' : 'Ne'}
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      </div>

      <Footer />
    </>
  );
}

export default JobsPage;
